import React, { useEffect, useState } from 'react';

import { Grid, Container, H1, H2, H3, P, Box, Type, Btn } from '@saladbob/sassafras';
import { motion } from 'framer-motion';

import dayjs from 'dayjs';

import { PiTrophy, PiToilet } from 'react-icons/pi';

import type { TeamDepth } from '../../types/Team';
import type { GameDepth } from '../../types/Game';

import Brackets from '../../components/brackets/Bracket';

// CRUD

import styles from '../../components/brackets/bracket.module.css';
import { fetchAdmin } from '../../hooks/useSwr';

const getTeamsKnockedOut = (games: GameDepth[], winner: TeamDepth) => {
    const knockedOutTeams = [];
    const eliminatedTeams = games.filter(game => game.winner?.id === winner.id);
    const teams = eliminatedTeams.reduce((acc, game) => {
        if (knockedOutTeams.indexOf(game.loser?.id) === -1) {
            knockedOutTeams.push(game.loser?.id);
            acc.push(game.loser);
        }
        return acc;
    }, []);
    // Split eliminated in half and deliver two arrays
    const half = Math.ceil(teams.length / 2);
    const firstHalf = teams.slice(0, half);
    const secondHalf = teams.slice(-half);
    return [firstHalf, secondHalf];
}

const EliminatedTeam = ({ name, font = 'h4', delay }: { name: string, delay: number, font?: string }) => (
    <Box>
        <motion.div
            initial={{ y: 0, color: '#000000' }}
            animate={{ y: 4, color: '#ccc' }}
            transition={{ delay, duration: 0.3 }}
        >
            <H3 font={font}>{name}</H3>
        </motion.div>
        <motion.div
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ delay, duration: 0.3 }}
            style={{ position: 'absolute', top: '50%' }}
        >
            <Box height="2px" width="100%" bgColor="black"/>
        </motion.div>
        
    </Box>
);

export async function getStaticPaths() {
    const courts = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/court?limit=10&depth=0&secret_key=${process.env.RATE_LIMIT_SECRET_KEY}`).then((r) => r.json());

    return {
        paths: courts.docs.map(court => ({ params: { id: court.id } })),
        fallback: 'blocking', // can also be true or 'blocking'
    }
}

export async function getStaticProps({ params }) {
    const court = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/court/${params.id}/get-court?secret_key=${process.env.RATE_LIMIT_SECRET_KEY}`).then((r) => r.json());

    if (!court || court.errors || !court.games || court.games.length === 0) {
        return {
            notFound: true,
        }
    }

    const tbGames = court.games.filter(game => game.title.includes('TB'));
    const games = court.games.filter(game => !game.title.includes('TB'));

    court.games = games;
    court.tbGames = tbGames;

    return {
        props: {
            metaTitle: court.label,
            court: court,
            teams: court.teams,
        },
    }
}

export default function Court({ court, teams }) {
    const [showBracket, setShowBracket] = useState(court.status !== 'finished');
    const [isChrome, setIsChrome] = useState(false);
    const { admin } = fetchAdmin();
    const padding = admin ? '74px' : null;

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const ua = window.navigator.userAgent;
        setIsChrome(/Chrome|CriOS/.test(ua) && !/Edg|OPR/.test(ua));
    }, []);

    const adminOptions = () => {
        if (admin) {
            return (
                <Box bgColor="neutral100" padding="sm" style={{ zIndex: 100, position: 'fixed', bottom: 0, left: 0 }}>
                    <Btn
                        tag="a"
                        color="black"
                        size="sm"
                        href={`${process.env.NEXT_PUBLIC_PAYLOAD_URL}/admin/collections/court/${court.id}`}
                        target="_blank"
                    >
                        View Court in Admin
                    </Btn>
                </Box>
            );
        }
    }

    if (!showBracket) {
        const [eliminated1, eliminated2] = getTeamsKnockedOut(court.games, court.firstPlace);
        return (
            <Box pb={padding}>
                {adminOptions()}
                <Box bgColor="white" padding={{ phone: 'sm', desktop: 'lg' }} textAlign="center" minHeight="100vh">
                    <Box justify="center" align="center" mb={{ phone: 'md', desktop: 'xl' }}>
                        <Grid columns="140px 1fr" align="center" gridGap="24px" ms="-164px" maxWidth="max-content" display={{ phone: 'none', desktop: 'grid' }}>
                            <img src="/images/macker_man_2025.svg" alt="Gus Macker" />
                            <div>
                                <H1 font={{ phone: 'h4', desktop: 'jumbo' }} uppercase>
                                    GUS MACKER {dayjs(court.tournament.start).format('YYYY')} {court.tournament.title}
                                </H1>
                                <H1 font={{ phone: 'p', desktop: 'h1' }} display="inline-flex" width="100%" justify="center" uppercase>
                                    <Type
                                        bgColor={court.color}
                                        color={court.textColor}
                                        padding="xs"
                                        ratio="1"
                                        rounded="100%"
                                        display="inline-flex"
                                        font="control"
                                        width="40px"
                                        verticalAlign="center"
                                        me="xs"
                                    >
                                        {court.title || court.label.split(' ')[1]}
                                    </Type>
                                    <span>
                                        ALL-WORLD TROPHY WINNERS
                                    </span>
                                </H1>
                            </div>
                        </Grid>
                        <Grid columns="80px 1fr" gridGap="32px" display={{ phone: 'grid', desktop: 'none' }}>
                            <Box pe="sm">
                                <img src="/images/macker_man_2025.svg" alt="Gus Macker" style={{ width: '100%' }} />
                            </Box>
                            <div>
                                <H1 font="h4" align="left" uppercase>
                                    GUS MACKER {dayjs(court.tournament.start).format('YYYY')} {court.tournament.title}
                                </H1>
                                <H1
                                    font="p"
                                    display="inline-flex"
                                    width="100%"
                                    align="left"
                                    uppercase
                                >
                                    <Type
                                        bgColor={court.color}
                                        color={court.textColor}
                                        rounded="100%"
                                        display="inline-flex"
                                        font="control"
                                        width="24px"
                                        height="24px"
                                        verticalAlign="center"
                                        align="center"
                                        me="xs"
                                    >
                                        {court.label.split(' ')[1]}
                                    </Type>
                                    <span>
                                        ALL-WORLD TROPHY WINNERS
                                    </span>
                                </H1>
                            </div>
                        </Grid>
                    </Box>
                    <Container size="lg">
                        <Grid columns="1fr max-content 1fr" align="center" gridGap="lg" display={{ phone: 'none', desktop: 'grid' }}>
                            <div>
                                {eliminated1.map((team, i) => (
                                    <EliminatedTeam key={team.name} name={team.name} delay={i * 1} />
                                ))}
                            </div>
                            <Box
                                margin="auto"
                                width="460px"
                                height="460px"
                                bgColor="white"
                                rounded="100%"
                                padding="xl"
                                bdWidth="16px"
                                bdColor="gold"
                                mb="lg"
                                overflow="hidden"
                            >
                                <Box txtColor="gold">
                                    <PiTrophy size={200} />
                                </Box>
                                <H2
                                    font="leader"
                                    color="white"
                                    bgColor="gold"
                                    padding={['xs', 'md']}
                                    width="max-content"
                                    rounded="md"
                                    margin="auto"
                                    mt="-38px"
                                    mb="md"
                                >
                                    1st Place
                                </H2>
                                <H2 font="h2" uppercase>{court.firstPlace.name}</H2>
                            </Box>
                            <div>
                                {eliminated2.map((team, i) => (
                                    <EliminatedTeam key={team.name} name={team.name} delay={(eliminated1.length + i) * 1} />
                                ))}
                            </div>
                        </Grid>
                        <Grid columns="1fr" align="center" gridGap="lg" display={{ phone: 'grid', desktop: 'none' }}>
                            <div>
                                <Box
                                    margin="auto"
                                    width="320px"
                                    bgColor="white"
                                    rounded="100%"
                                    padding="xl"
                                    bdWidth="16px"
                                    bdColor="gold"
                                    verticalAlign="center"
                                    mb="lg"
                                    style={{ aspectRatio: '1 / 1' }}
                                >
                                    <Box txtColor="gold">
                                        <PiTrophy size={120} />
                                    </Box>
                                    <H2
                                        font="leader"
                                        color="white"
                                        bgColor="gold"
                                        padding={['xs', 'md']}
                                        width="max-content"
                                        rounded="md"
                                        margin="auto"
                                        mt="-28px"
                                        mb="sm"
                                    >
                                        1st Place
                                    </H2>
                                    <H2 font="h3" uppercase>{court.firstPlace.name}</H2>
                                </Box>
                            </div>
                        </Grid>
                    </Container>
                    <Container size="lg">
                        <Grid columns={{ phone: '1fr', desktop: '1fr 1fr 1fr'}} gridGap="lg">
                            <Box
                                bgColor="white"
                                verticalAlign="center"
                            >
                                <Box txtColor="sienna" mt="-16px" display={{ phone: 'none', desktop: 'block' }}>
                                    <PiTrophy size={120} />
                                </Box>
                                <Box txtColor="sienna" display={{ phone: 'block', desktop: 'none' }}>
                                    <PiTrophy size={64} />
                                </Box>
                                <H3 font="p" color="neutral" weight="lighter">3rd Place</H3>
                                <H3 font="h4" mt="-8px">{court.thirdPlace.name}</H3>
                            </Box>
                            <Box
                                bgColor="white"
                                verticalAlign="center"
                                order={{ phone: -1, desktop: 0 }}
                            >
                                <Box txtColor="neutral400" mt="-16px" display={{ phone: 'none', desktop: 'block' }}>
                                    <PiTrophy size={120} />
                                </Box>
                                <Box txtColor="neutral400" display={{ phone: 'block', desktop: 'none' }}>
                                    <PiTrophy size={64} />
                                </Box>
                                <H3 font="p" color="neutral" weight="lighter">2nd Place</H3>
                                <H3 font="h4" mt="-8px">{court.secondPlace.name}</H3>
                            </Box>
                            <Box
                                bgColor="white"
                                verticalAlign="center"
                            >
                                <Box txtColor="neutral200" mt="-8px" display={{ phone: 'none', desktop: 'block' }}>
                                    <PiToilet size={120} />
                                </Box>
                                <Box txtColor="neutral200" display={{ phone: 'block', desktop: 'none' }}>
                                    <PiToilet size={64} />
                                </Box>
                                <H3 font="p" color="neutral" weight="lighter">Toilet Bowl</H3>
                                <H3 font="h4" mt="-8px">{court.tbPlace.name}</H3>
                            </Box>
                        </Grid>
                    </Container>
                    <Box mt="xl" textAlign="center">
                        <Btn onClick={() => setShowBracket(true)}>
                            View Brackets
                        </Btn>
                    </Box>
                </Box>
            </Box>
        );
    }
    return (
        <Box pb={padding} className={`${styles.bracketWrapper}`}>
            {adminOptions()}
            <div className={`${styles.bracketContainer} ${styles['bracket' + court.teams.length]}`}>
                <Grid
                    className={styles.courtHeader}
                    columnGap="md"
                    columns="minMax(max-content, 1fr) max-content max-content"
                    padding={['xl', 'xl', 'lg', 'xl']}
                    height="280px"
                >
                    <div>
                        <P className={styles.tournamentName} color="primary" font="h1" uppercase weight="bold" mb="0">
                            {court.tournament.title} - GUS MACKER 
                        </P>
                        <P className={styles.courtMeta} mb="0" font="leader" weight="bold" uppercase>
                            COURT: {court.title || court.label}
                            {court.revised && (
                                <Type display="inline-block" font="p" ps="xs">(Revised {dayjs(court.revised).format('MM/DD/YYYY h:mm a')})</Type>
                            )}
                        </P>
                        <P className={styles.courtMeta} mb="0" font="leader" weight="bold"  >TEAMS: {court.teams.length}</P>
                        { court.status === 'finished' && (
                            <Btn size="sm" mt="md" onClick={() => setShowBracket(false)}>
                                View Winners
                            </Btn>
                        )}
                    </div>
                    <div>
                        <img className={styles.mackerMan} src="/images/macker-man-court.svg" alt="Gus Macker Basketball" />
                    </div>
                    {teams.length > 5 && (
                        <Box>
                            <Grid columns="1fr 1fr" gridGap="xs" bdWidth="0 0 2px 0" width="100%" bdColor="secondary" txtColor="secondary">
                                <P mb="0" font="small" className={styles.teamName} weight="bold" uppercase>TEAM</P>
                                <P mb="0" font="small" className={styles.captainName} weight="bold" uppercase >CAPTAIN</P>
                            </Grid>
                            {teams.map((team) => (
                                <Grid key={team.id} columns="1fr 1fr" gridGap="xs" bdWidth="0 0 1px 0" bdColor="neutral200">
                                    <P mb="0" font="small" className={styles.teamName} weight="bold" uppercase>
                                        {team.name}
                                    </P>
                                    <P mb="0" font="small" className={styles.captainName} weight="bold" uppercase>
                                        {team.captain}
                                    </P>
                                </Grid>
                            ))}
                        </Box>
                    )}
                </Grid>
                <Brackets court={court} />
            </div>
        </Box>
    );
};
